import axios, { AxiosError, AxiosResponse } from 'axios';
import { message } from 'antd';

// 从环境变量读取 API 地址
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * 错误消息映射 - 将英文错误翻译为中文
 */
const ERROR_MESSAGE_MAP: Record<string, string> = {
  'Internal Server Error': '系统内部错误',
  'Validation Error': '数据验证失败',
  'Database Error': '数据库错误',
  'Permission Denied': '权限不足',
  'Unauthorized': '未授权',
  'Not Found': '资源不存在',
  'Conflict': '数据冲突',
  'password too weak': '密码强度不足',
  'already exists': '已存在',
  'duplicate key': '数据重复',
};

/**
 * 翻译错误消息
 */
function translateErrorMessage(msg: string): string {
  if (!msg) return '操作失败';

  const lowerMsg = msg.toLowerCase();
  for (const [en, zh] of Object.entries(ERROR_MESSAGE_MAP)) {
    if (lowerMsg.includes(en.toLowerCase())) {
      return zh;
    }
  }

  return msg;
}

/**
 * Axios 客户端实例
 * 统一配置请求拦截、响应拦截和错误处理
 */
export const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 300000, // 300秒超时（5分钟，排班生成需要较长时间）
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    // 添加 Token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 添加 TenantID (多租户支持)
    const tenantId = localStorage.getItem('tenant_id');
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<{ detail?: string; message?: string; error?: string }>) => {
    const status = error.response?.status;
    let errorMessage = '请求失败';

    if (error.response?.data) {
      const data = error.response.data;

      // 优先使用 detail 字段（后端主要错误信息）
      if (typeof data.detail === 'string') {
        errorMessage = translateErrorMessage(data.detail);
      } else if (Array.isArray(data.detail)) {
        // FastAPI Validation Error
        errorMessage = data.detail
          .map((item: any) => {
            const loc = item.loc?.[item.loc.length - 1] || '';
            const msg = item.msg || '验证失败';
            return loc ? `${loc}: ${msg}` : msg;
          })
          .join('; ');
      } else if (data.message) {
        errorMessage = translateErrorMessage(data.message);
      } else if (data.error) {
        errorMessage = translateErrorMessage(data.error);
      }
    } else if (error.message) {
      errorMessage = translateErrorMessage(error.message);
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = '网络连接失败，请检查网络后重试';
    } else if (error.code === 'ETIMEDOUT') {
      errorMessage = '请求超时，请稍后重试';
    }

    // 特殊处理登录接口的业务错误，交由登录页面自己处理，避免重复弹窗
    const isLoginRequest =
      error.config?.url?.includes('/auth/login') || error.config?.url?.includes('/login');
    if (isLoginRequest && (status === 400 || status === 401 || status === 422)) {
      return Promise.reject(error);
    }

    // 401 未授权 - 跳转到登录页
    if (status === 401) {
      // 清除本地存储
      localStorage.removeItem('token');
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('user_info');

      // 如果不是登录页面，跳转到登录页
      if (!window.location.pathname.includes('/login')) {
        message.warning('登录已过期，请重新登录');
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    // 403 禁止访问
    if (status === 403) {
      message.error(errorMessage);
      return Promise.reject(error);
    }

    // 404 资源不存在
    if (status === 404) {
      message.error(errorMessage);
      return Promise.reject(error);
    }

    // 500 服务器错误 - 显示更友好的提示
    if (status === 500) {
      message.error(errorMessage + '，请稍后重试或联系管理员');
      return Promise.reject(error);
    }

    // 其他错误显示提示
    if (status !== 401) {
      message.error(errorMessage);
    }

    return Promise.reject(error);
  }
);

/**
 * 检查 API 服务状态
 */
export const checkApiStatus = async (): Promise<boolean> => {
  try {
    await apiClient.get('/sample-data/');
    return true;
  } catch {
    return false;
  }
};

/**
 * 检查是否已登录
 */
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('token');
  return !!token;
};

export default apiClient;
